// Install, move or return a sensor (demo: partner stock + the installer's
// mobile "Install" screen): customer account, store labels, cabinet name,
// regime -> owner move + labels + installation record (data/installs.ts).
import { useState } from "preact/hooks";
import { classKeys, clsName } from "../data/classes";
import { useT } from "../i18n";
import { accounts, tree } from "../lib/account";
import { fleet, loadFleet } from "../data/fleet";
import { install } from "../data/installs";
import type { Point } from "../data/locations";
import { errText } from "../lib/apierr";

/** the customer (or the root) an account belongs to */
const customerRoot = (id: string | null | undefined) => {
  const byId = new Map(accounts.value.map((a) => [a.id, a]));
  let a = id ? byId.get(id) : undefined;
  while (a && a.kind !== "customer" && a.parent_id) a = byId.get(a.parent_id);
  return a?.id || null;
};

export function InstallDialog({ p, mode, onClose }: { p: Point; mode: "install" | "return"; onClose: (done: boolean) => void }) {
  const t = useT();
  const root = accounts.value.find((a) => !a.parent_id);
  const lb = (k: string) => String(p.labels[k] || "");
  const [f, setF] = useState({ account_id: p.owner && p.owner !== root?.id ? p.owner : tree.value.find((r) => r.a.kind === "customer")?.a.id || "", city: lb("city"), area: lb("area"), site: lb("site"), cabinet: p.name === p.serial ? "" : p.name, regime: p.regime || "frozen", room: p.kind === "room" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const vals = (k: string) => [...new Set((fleet.value?.points || []).map((x) => String(x.labels[k] || "")).filter(Boolean))];
  const set = (k: string) => (e: Event) => setF({ ...f, [k]: (e.target as HTMLInputElement).value });
  const go = async (e: Event) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (mode === "return") await install(p.id, p.owner || null, { account_id: root!.id, city: "", area: "", site: "", cabinet: p.serial, regime: "", stock: true }, "return");
      else {
        const same = customerRoot(f.account_id) && customerRoot(f.account_id) === customerRoot(p.owner);
        await install(p.id, p.owner || null, { ...f }, same ? "reorg" : "transfer");
      }
      await loadFleet(t.lang);
      onClose(true);
    } catch (x) { setErr(errText(t, x)); }
    setBusy(false);
  };
  return (
    <div class="modal" onClick={(e) => e.target === e.currentTarget && onClose(false)}>
      <form class="box card" style="width:min(620px,100%)" onSubmit={go}>
        <h2 style="margin:0">{mode === "return" ? t("x.inst.returnTitle") : t("x.inst.title")}</h2>
        <p style="margin:0;color:var(--muted)">{p.serial} · {mode === "return" ? t("x.inst.returnText") : t("x.inst.text")}</p>
        {mode === "install" && <>
          <label class="f">{t("app.customer")}<select required value={f.account_id} onChange={set("account_id")}>{tree.value.filter((r) => r.a.parent_id).map(({ a, depth }) => <option key={a.id} value={a.id}>{"  ".repeat(depth - 1)}{a.name}</option>)}</select></label>
          {(fleet.value?.stores || []).filter((x) => x.account_id === f.account_id).length > 0 && (
            <label class="f">{t("lvl.site")}<select value={f.site} onChange={(e) => { const v = (e.target as HTMLSelectElement).value; const st = fleet.value!.stores.find((x) => x.value === v); if (st) setF({ ...f, site: st.value, city: st.city, area: st.area }); }}>
              <option value="">{t("x.store.pick")}</option>
              {fleet.value!.stores.filter((x) => x.account_id === f.account_id).map((x) => <option key={x.value} value={x.value}>{x.name}{x.address ? " · " + x.address : ""}</option>)}
            </select></label>
          )}
          <div class="g3" style="gap:10px">
            {(["city", "area", "site"] as const).map((k) => <label key={k} class="f">{t("lvl." + k)}<input required list={"in-" + k} value={f[k]} onInput={set(k)} /><datalist id={"in-" + k}>{vals(k).map((v) => <option key={v} value={v} />)}</datalist></label>)}
          </div>
          <div class="seg" role="group">{[false, true].map((r) => <button key={String(r)} type="button" class={f.room === r ? "on" : ""} onClick={() => setF({ ...f, room: r })}>{r ? t("x.room.where") : t("x.inst.cabinet")}</button>)}</div>
          {!f.room && (fleet.value?.cabinets || []).filter((c) => c.site === f.site).length > 0 && (
            <label class="f">{t("x.inst.cabinet")}<select value="" onChange={(e) => { const c = fleet.value!.cabinets.find((x) => x.id === (e.target as HTMLSelectElement).value); if (c) setF({ ...f, cabinet: c.name, regime: c.regime || f.regime }); }}>
              <option value="">{t("x.cab.pick")}</option>
              {fleet.value!.cabinets.filter((c) => c.site === f.site).map((c) => <option key={c.id} value={c.id}>{c.name}{c.regime ? " · " + clsName(t.lang, c.regime) : ""}</option>)}
            </select></label>
          )}
          <div class="g3" style="grid-template-columns:1fr 1fr;gap:10px">
            <label class="f">{f.room ? t("x.room.name") : t("x.inst.cabinet")}<input required value={f.cabinet} placeholder={f.room ? t("x.room.example") : "FI-03"} onInput={set("cabinet")} /></label>
            <label class="f">{t("x.regime")}<select value={f.regime} onChange={set("regime")}>{classKeys().map((r) => <option key={r} value={r}>{clsName(t.lang, r)}</option>)}</select></label>
          </div>
          <p style="margin:0;color:var(--muted);font-size:13px">{t("x.inst.note")}</p>
        </>}
        {err && <p class="st st-crit" style="margin:0">{err}</p>}
        <div class="row" style="justify-content:flex-end"><button class="btn" type="button" onClick={() => onClose(false)}>{t("alm.cancel")}</button><button class="btn pri" type="submit" disabled={busy}>{mode === "return" ? t("x.inst.returnBtn") : t("x.inst.btn")}</button></div>
      </form>
    </div>
  );
}
